<script setup lang="ts">
import type { Product } from '~/models/Product'
import type { AggregationTermEntry, SearchResult } from '~/models/Content/Response'
import { useRoute }  from '#imports'
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { sortBySubjectIndex } from '~/utils/subjectSorter'
import { gradeTypeMapping } from '~/utils/gradeSorter'
import arrayUtils from '~/utils/arrayUtils'
import useProductStore from '~/stores/product'
import useAuthStore from '~/stores/auth'
import useText from '~/composables/useText'
import useAggregations from '~/composables/useSearchAggregations'
import useProductHelper from '~/composables/useProductHelper'
import useAggregationMapper from '~/composables/useAggregationMapper'
import SearchSubjectFilterBox from '~/components/search/SearchSubjectFilterBox.vue'
import FilterBox from '~/components/search/SearchFilterBox.vue'
import GlobalSearchGradeFilterBox from '~/components/search/global/GlobalSearchGradeFilterBox.vue'
import AggregationButton from '~/components/search/AggregationButton.vue'
import Button from "~/components/ui/Button.vue";
import { faArrowLeft } from "@fortawesome/duotone-regular-svg-icons";
import AuSkeletonWrapper from "~/components/ui/AuSkeletonWrapper.vue";
import AuSkeleton from "~/components/ui/AuSkeleton.vue";
import AuCheckbox from "~/components/ui/AuCheckbox.vue";

interface GroupedGradeAggregation {
  group: string
  keys: { key: string, count: number }[]
}

const props = defineProps<{
  results: SearchResult
  isLoading: boolean
  isRefetching: boolean
  criterions: Record<string, string[]>
  products: Product[]
}>()

const emit = defineEmits(['toggleCriterion'])

const resultsRef = ref(props.results)
const showFilters = ref(false)

const { t } = useI18n()
const { unique, intersect } = arrayUtils()
const { capitalize } = useText()
const { isTeacher, userRelevantGrades } = storeToRefs(useAuthStore())
const { filteredSupportProducts } = storeToRefs(useProductStore())
const { isRelevantProduct } = useProductHelper()
const { mapGroupedGradeAggregations } = useAggregationMapper()
const route = useRoute()
const breakpoints = useBreakpoints(breakpointsTailwind)

const mdBreakpoint = breakpoints.greater('md')

const {
  labelAggregations,
  accessAggregations,
  activityAggregations,
  sortedGradeAggregations,
  genreAggregations,
  tieredAggregations,
} = useAggregations(resultsRef)

const groupGradeAggregations = computed(() => mapGroupedGradeAggregations(sortedGradeAggregations.value, gradeTypeMapping))

const groupedGradeAggregations = computed((): GroupedGradeAggregation[] => {
  return Array.from(groupGradeAggregations.value.entries()).map(([group, entries]) => ({
    group,
    keys: entries.map(entry => ({
      key: entry.key,
      count: entry.count,
    }))
  }))
})

const matchingSubjects = computed(() => {
  return props.products
    .filter(product => props.criterions.grade.length
      ? intersect(product.grades, props.criterions.grade).length > 0
      : isRelevantProduct(product, userRelevantGrades.value))
    .flatMap(({ subjects }) => subjects)
    .filter(unique)
    .sort(sortBySubjectIndex)
})

watch(() => props.results, (newResults) => {
  resultsRef.value = newResults
})
</script>

<template>
  <aside class="grid-in-filters">
    <div>
      <Button
        v-if="!mdBreakpoint"
        variant="outline"
        @click="showFilters = !showFilters"
      >
        {{ showFilters ? t('filters.hideFilters') : t('filters.chooseFilters') }}
      </Button>
      <div
        v-if="(!isLoading && mdBreakpoint) || (!isLoading && showFilters)"
        class="mt-4 flex flex-col gap-4"
      >
        <Button
          v-if="route.query.fromSubject"
          :to="{ name: 'subject', params: { subject: String(route.query.fromSubject).toLowerCase() } }"
          class="self-start"
        >
          <font-awesome-icon :icon="faArrowLeft" />
          {{ t('backTo', { name: t(`metadata.subjects.${route.query.fromSubject}`).toLowerCase() }) }}
        </Button>
        <div
          v-if="filteredSupportProducts.length"
          class="rounded-xl bg-white"
        >
          <AggregationButton
            :name="t('search.filterBoxes.support')"
            type="label"
            :disabled="isRefetching"
            stretch
          >
            <AuCheckbox
              id="has_audio"
              class="shrink-0"
              :model-value="criterions.support?.includes('true')"
              :disabled="isRefetching"
              @update:model-value="() => emit('toggleCriterion', 'support', 'true')"
            />
          </AggregationButton>
        </div>

        <div
          v-if="tieredAggregations.length"
          class="rounded-xl bg-white"
        >
          <AggregationButton
            :name="t('search.checkboxes.tiered')"
            :entry="tieredAggregations[0]"
            type="label"
            :disabled="isRefetching"
            stretch
          >
            <AuCheckbox
              id="tiered"
              type="checkbox"
              class="shrink-0"
              :model-value="criterions.checkbox.some(key => key === 'tiered')"
              :disabled="isRefetching"
              @update:model-value="() => emit('toggleCriterion', 'checkbox', 'tiered')"
            />
          </AggregationButton>
        </div>

        <SearchSubjectFilterBox
          v-if="matchingSubjects.length"
          :title="t('filters.subject')"
          :subjects="matchingSubjects"
          :is-refetching="isRefetching"
          :selected-subjects="criterions.subject ?? []"
          @toggle-criterion="(subjectCode) => emit('toggleCriterion', 'subject', subjectCode)"
        />
        <GlobalSearchGradeFilterBox
          :title="t('filters.grade')"
          :is-refetching="isRefetching"
          :criterions="criterions.grade ?? []"
          :aggregations="groupedGradeAggregations"
          :translator="(entry) => t(`metadata.grades.${entry.key}`)"
          @toggle-criterion="(entry) => emit('toggleCriterion', 'grade', entry)"
        />
        <FilterBox
          v-if="activityAggregations.length"
          :title="t('labels.activity')"
          :is-refetching="isRefetching"
          :aggregations="activityAggregations"
          :criterions="criterions.activity ?? []"
          :translator="(entry: AggregationTermEntry) => t(`activities.${entry.key}`, capitalize(entry.key))"
          @toggle-criterion="(entry) => emit('toggleCriterion', 'activity', entry)"
        />
        <FilterBox
          v-if="labelAggregations.length"
          :title="t('filters.taskType')"
          :is-refetching="isRefetching"
          :aggregations="labelAggregations"
          :criterions="criterions.label ?? []"
          :translator="(entry: AggregationTermEntry) => t(`labels.${entry.key}`)"
          @toggle-criterion="(entry) => emit('toggleCriterion', 'label', entry)"
        />
        <FilterBox
          v-if="genreAggregations.length"
          :title="t('filters.genre')"
          :is-refetching="isRefetching"
          :aggregations="genreAggregations"
          :criterions="criterions.genre ?? []"
          :translator="(entry: AggregationTermEntry) => t(`genre.${entry.key.toLowerCase()}`, capitalize(entry.key))"
          @toggle-criterion="(entry) => emit('toggleCriterion', 'genre', entry)"
        />
        <FilterBox
          v-if="isTeacher && accessAggregations.length"
          :title="t('access.title')"
          :is-refetching="isRefetching"
          :aggregations="accessAggregations"
          :criterions="criterions.access ?? []"
          :translator="(entry: AggregationTermEntry) => t(`access.${entry.key}`)"
          @toggle-criterion="(entry) => emit('toggleCriterion', 'access', entry)"
        />
      </div>
      <AuSkeletonWrapper
        v-if="isLoading"
        class="flex flex-col gap-4"
      >
        <AuSkeleton
          v-for="i in 6"
          :key="i"
          height="45px"
          width="100%"
        />
      </AuSkeletonWrapper>
    </div>
  </aside>
</template>
